<template>
  <div class="progress-tracker">
    <!-- Timeframe Selector -->
    <div class="timeframe-selector">
      <div class="selector-header">
        <h3 class="section-title">
          <font-awesome-icon icon="chart-line" />
          {{ $t('progress.title') || 'Progress Tracker' }}
        </h3>
      </div>

      <div class="timeframe-buttons">
        <button
          v-for="timeframe in timeframes"
          :key="timeframe.value"
          :class="['timeframe-btn', { active: selectedTimeframe === timeframe.value }]"
          @click="$emit('timeframe-change', timeframe.value)"
        >
          {{ timeframe.label }}
        </button>
      </div>

      <!-- Event Selector (if showing event progress) -->
      <div v-if="selectedTimeframe === 'event' && selectedEvent" class="event-info">
        <div class="event-card">
          <h4 class="event-name">{{ selectedEvent.name }}</h4>
          <div class="event-dates">
            <span>{{ formatEventDate(selectedEvent.from * 1000) }}</span>
            <span>→</span>
            <span>{{ formatEventDate(selectedEvent.to * 1000) }}</span>
          </div>
          <div class="event-progress-bar">
            <div class="progress-fill" :style="{ width: eventProgress + '%' }"></div>
          </div>
          <div class="event-stats">
            <span class="progress-percent">{{ eventProgress }}% Complete</span>
            <span class="days-remaining">{{ daysRemaining }} days remaining</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p>{{ $t('progress.loading') || 'Loading progress data...' }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <font-awesome-icon icon="exclamation-triangle" class="error-icon" />
      <p>{{ error }}</p>
    </div>

    <!-- Progress Data -->
    <div v-else-if="progressData" class="progress-content">
      <!-- Key Metrics Cards -->
      <div class="metrics-grid">
        <div class="metric-card">
          <div class="metric-icon distance">
            <font-awesome-icon icon="route" />
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ metrics.totalDistance }} km</div>
            <div class="metric-label">{{ $t('progress.totalDistance') || 'Total Distance' }}</div>
            <div v-if="comparison.distanceChange !== 0" :class="['metric-change', comparison.distanceChange > 0 ? 'positive' : 'negative']">
              <font-awesome-icon :icon="comparison.distanceChange > 0 ? 'arrow-up' : 'arrow-down'" />
              {{ Math.abs(comparison.distanceChange) }}% vs previous period
            </div>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon time">
            <font-awesome-icon icon="clock" />
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ formatTime(metrics.totalTime * 60) }}</div>
            <div class="metric-label">{{ $t('progress.totalTime') || 'Total Time' }}</div>
            <div v-if="comparison.timeChange !== 0" :class="['metric-change', comparison.timeChange > 0 ? 'positive' : 'negative']">
              <font-awesome-icon :icon="comparison.timeChange > 0 ? 'arrow-up' : 'arrow-down'" />
              {{ Math.abs(comparison.timeChange) }}% vs previous period
            </div>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon activities">
            <font-awesome-icon icon="running" />
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ metrics.totalActivities }}</div>
            <div class="metric-label">{{ $t('progress.activities') || 'Activities' }}</div>
            <div v-if="comparison.activitiesChange !== 0" :class="['metric-change', comparison.activitiesChange > 0 ? 'positive' : 'negative']">
              <font-awesome-icon :icon="comparison.activitiesChange > 0 ? 'arrow-up' : 'arrow-down'" />
              {{ Math.abs(comparison.activitiesChange) }} vs previous period
            </div>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon elevation">
            <font-awesome-icon icon="mountain" />
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ metrics.totalElevation }} m</div>
            <div class="metric-label">{{ $t('progress.totalElevation') || 'Total Elevation' }}</div>
          </div>
        </div>
      </div>

      <!-- Additional Metrics -->
      <div class="additional-metrics">
        <div class="metric-row">
          <div class="metric-item">
            <font-awesome-icon icon="tachometer-alt" class="item-icon" />
            <span class="item-label">{{ $t('progress.avgPace') || 'Avg Pace' }}:</span>
            <span class="item-value">{{ metrics.averagePace }} min/km</span>
          </div>
          <div class="metric-item">
            <font-awesome-icon icon="heartbeat" class="item-icon" />
            <span class="item-label">{{ $t('progress.avgHR') || 'Avg Heart Rate' }}:</span>
            <span class="item-value">{{ metrics.averageHeartRate }} bpm</span>
          </div>
        </div>
        <div class="metric-row">
          <div class="metric-item" v-if="metrics.longestRun > 0">
            <font-awesome-icon icon="running" class="item-icon" />
            <span class="item-label">{{ $t('progress.longestRun') || 'Longest Run' }}:</span>
            <span class="item-value">{{ metrics.longestRun }} km</span>
          </div>
          <div class="metric-item" v-if="metrics.longestRide > 0">
            <font-awesome-icon icon="biking" class="item-icon" />
            <span class="item-label">{{ $t('progress.longestRide') || 'Longest Ride' }}:</span>
            <span class="item-value">{{ metrics.longestRide }} km</span>
          </div>
        </div>
      </div>

      <!-- Activity Breakdown by Type -->
      <div class="breakdown-section">
        <h4 class="breakdown-title">
          <font-awesome-icon icon="chart-pie" />
          {{ $t('progress.byType') || 'Activities by Type' }}
        </h4>
        <div class="breakdown-cards">
          <div
            v-for="type in breakdown.byType"
            :key="type.type"
            class="breakdown-card"
          >
            <div class="breakdown-header">
              <font-awesome-icon :icon="getActivityIcon(type.type)" />
              <span class="breakdown-type">{{ type.type }}</span>
            </div>
            <div class="breakdown-stats">
              <div class="breakdown-stat">
                <span class="stat-label">Count:</span>
                <span class="stat-value">{{ type.count }}</span>
              </div>
              <div class="breakdown-stat">
                <span class="stat-label">Distance:</span>
                <span class="stat-value">{{ type.distance }} km</span>
              </div>
              <div class="breakdown-stat">
                <span class="stat-label">Time:</span>
                <span class="stat-value">{{ Math.round(type.time) }} min</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Weekly Trend Chart -->
      <div class="chart-section">
        <h4 class="chart-title">
          <font-awesome-icon icon="chart-bar" />
          {{ $t('progress.weeklyTrend') || 'Weekly Trend' }}
        </h4>
        <div class="simple-chart">
          <div class="chart-bars">
            <div
              v-for="week in breakdown.byWeek"
              :key="week.week"
              class="chart-bar-container"
            >
              <div
                class="chart-bar"
                :style="{ height: getBarHeight(week.distance, maxWeeklyDistance) + '%' }"
                :title="`${week.week}: ${week.distance}km`"
              >
                <span class="bar-label">{{ week.distance }}</span>
              </div>
              <span class="bar-week">{{ formatWeekLabel(week.week) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <font-awesome-icon icon="chart-line" class="empty-icon" />
      <p>{{ $t('progress.noData') || 'No activity data available for this period' }}</p>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { formatTime } from '@/utils/formatters'

export default {
  name: 'ProgressTracker',

  components: {
    FontAwesomeIcon
  },

  props: {
    /**
     * Progress data from progressMixin
     */
    progressData: {
      type: Object,
      default: null
    },

    /**
     * Loading state
     */
    loading: {
      type: Boolean,
      default: false
    },

    /**
     * Error message
     */
    error: {
      type: String,
      default: null
    },

    /**
     * Progress metrics
     */
    metrics: {
      type: Object,
      default: () => ({})
    },

    /**
     * Activity breakdown
     */
    breakdown: {
      type: Object,
      default: () => ({ byType: [], byWeek: [], byDay: [] })
    },

    /**
     * Comparison data
     */
    comparison: {
      type: Object,
      default: () => ({})
    },

    /**
     * Selected timeframe
     */
    selectedTimeframe: {
      type: String,
      default: 'last_30_days'
    },

    /**
     * Selected event (if timeframe is 'event')
     */
    selectedEvent: {
      type: Object,
      default: null
    },

    /**
     * Event progress percentage
     */
    eventProgress: {
      type: Number,
      default: 0
    },

    /**
     * Days remaining in event
     */
    daysRemaining: {
      type: Number,
      default: 0
    }
  },

  data () {
    return {
      timeframes: [
        { value: 'last_7_days', label: 'Last 7 Days' },
        { value: 'last_30_days', label: 'Last 30 Days' },
        { value: 'last_90_days', label: 'Last 90 Days' },
        { value: 'event', label: 'Event Period' }
      ]
    }
  },

  computed: {
    /**
     * Get maximum weekly distance for chart scaling
     */
    maxWeeklyDistance () {
      if (!this.breakdown.byWeek || this.breakdown.byWeek.length === 0) return 100
      return Math.max(...this.breakdown.byWeek.map(w => w.distance))
    }
  },

  methods: {
    formatTime,

    /**
     * Format event date
     */
    formatEventDate (timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    /**
     * Get activity icon
     */
    getActivityIcon (type) {
      const icons = {
        'Run': 'running',
        'VirtualRun': 'running',
        'Ride': 'biking',
        'VirtualRide': 'biking',
        'Swim': 'swimmer',
        'Walk': 'walking',
        'Hike': 'hiking'
      }
      return icons[type] || 'running'
    },

    /**
     * Calculate bar height for chart
     */
    getBarHeight (value, max) {
      if (max === 0) return 0
      return (value / max) * 100
    },

    /**
     * Format week label
     */
    formatWeekLabel (weekKey) {
      // Format: 2025-W01 -> W01
      return weekKey.split('-')[1]
    }
  }
}
</script>

<style scoped>
.progress-tracker {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
}

/* Timeframe Selector */
.timeframe-selector {
  margin-bottom: 2rem;
}

.selector-header {
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.timeframe-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.timeframe-btn {
  padding: 0.5rem 1rem;
  border: 2px solid #e0e0e0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  -webkit-tap-highlight-color: rgba(252, 76, 2, 0.1);
  touch-action: manipulation;
}

.timeframe-btn:hover {
  border-color: #FC4C02;
  color: #FC4C02;
}

.timeframe-btn:active {
  transform: scale(0.98);
}

.timeframe-btn.active {
  background: #FC4C02;
  color: white;
  border-color: #FC4C02;
}

/* Event Info */
.event-info {
  margin-top: 1rem;
}

.event-card {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.event-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
}

.event-dates {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  margin-bottom: 1rem;
}

.event-progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #FC4C02 0%, #FF6B35 100%);
  transition: width 0.3s;
}

.event-stats {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
}

.progress-percent {
  color: #FC4C02;
  font-weight: 600;
}

.days-remaining {
  color: #666;
}

/* Loading/Error States */
.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 3rem;
}

.loading-state p,
.error-state p,
.empty-state p {
  margin-top: 1rem;
  color: #666;
}

.error-icon,
.empty-icon {
  font-size: 3rem;
  color: #ccc;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.metric-card {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 1rem;
}

.metric-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.metric-icon.distance { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.metric-icon.time { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
.metric-icon.activities { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.metric-icon.elevation { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
}

.metric-label {
  color: #666;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.metric-change {
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.metric-change.positive { color: #28a745; }
.metric-change.negative { color: #dc3545; }

/* Additional Metrics */
.additional-metrics {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.metric-row {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}

.metric-row:last-child {
  margin-bottom: 0;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.item-icon {
  color: #FC4C02;
}

.item-label {
  color: #666;
  font-size: 0.875rem;
}

.item-value {
  font-weight: 600;
  color: #333;
}

/* Breakdown Section */
.breakdown-section {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.breakdown-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.breakdown-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.breakdown-card {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s;
  -webkit-tap-highlight-color: rgba(252, 76, 2, 0.1);
}

.breakdown-card:hover {
  border-color: #FC4C02;
  transform: translateY(-2px);
}

@media (hover: none) {
  .breakdown-card:active {
    border-color: #FC4C02;
    transform: scale(0.98);
  }
}

.breakdown-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #FC4C02;
}

.breakdown-type {
  font-weight: 700;
  color: #333;
}

.breakdown-stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.breakdown-stat {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
}

.stat-label {
  color: #666;
}

.stat-value {
  font-weight: 600;
  color: #333;
}

/* Chart Section */
.chart-section {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.simple-chart {
  min-height: 200px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  height: 200px;
  padding: 0 1rem;
  min-width: min-content;
}

.chart-bar-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  height: 100%;
  justify-content: flex-end;
}

.chart-bar {
  width: 100%;
  background: linear-gradient(180deg, #FC4C02 0%, #FF6B35 100%);
  border-radius: 8px 8px 0 0;
  transition: all 0.3s;
  position: relative;
  min-height: 20px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.5rem;
}

.chart-bar:hover {
  opacity: 0.8;
}

.bar-label {
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
}

.bar-week {
  font-size: 0.75rem;
  color: #666;
  transform: rotate(-45deg);
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 768px) {
  .progress-tracker {
    padding: 1rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .metric-card {
    padding: 1rem;
  }

  .metric-value {
    font-size: 1.5rem;
  }

  .metric-row {
    flex-direction: column;
    gap: 1rem;
  }

  .breakdown-cards {
    grid-template-columns: 1fr;
  }

  .chart-bars {
    gap: 0.5rem;
    padding: 0 0.5rem;
  }

  .bar-week {
    font-size: 0.65rem;
  }
}

/* Small devices (iPhone SE, small phones) */
@media (max-width: 480px) {
  .progress-tracker {
    padding: 0.75rem;
    background: #f8f9fa;
  }

  /* Timeframe Selector */
  .section-title {
    font-size: 1.1rem;
    gap: 0.35rem;
  }

  .timeframe-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .timeframe-btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    white-space: nowrap;
  }

  /* Event Info */
  .event-card {
    padding: 1rem;
  }

  .event-name {
    font-size: 1rem;
  }

  .event-dates {
    font-size: 0.875rem;
    flex-wrap: wrap;
  }

  .event-stats {
    flex-direction: column;
    gap: 0.25rem;
    align-items: flex-start;
  }

  /* Metrics Grid */
  .metrics-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .metric-card {
    padding: 0.875rem;
    gap: 0.75rem;
  }

  .metric-icon {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  .metric-value {
    font-size: 1.35rem;
    line-height: 1.2;
  }

  .metric-label {
    font-size: 0.8rem;
  }

  .metric-change {
    font-size: 0.7rem;
  }

  /* Additional Metrics */
  .additional-metrics {
    padding: 1rem;
  }

  .metric-row {
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .metric-item {
    font-size: 0.875rem;
  }

  .item-label {
    font-size: 0.8rem;
  }

  .item-value {
    font-size: 0.9rem;
  }

  /* Breakdown Section */
  .breakdown-section {
    padding: 1rem;
  }

  .breakdown-title {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  .breakdown-cards {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .breakdown-card {
    padding: 0.875rem;
  }

  .breakdown-stat {
    font-size: 0.8rem;
  }

  /* Chart Section */
  .chart-section {
    padding: 1rem;
  }

  .chart-title {
    font-size: 1rem;
    margin-bottom: 1rem;
  }

  .simple-chart {
    min-height: 150px;
    padding-bottom: 0.5rem;
  }

  .chart-bars {
    height: 150px;
    gap: 0.35rem;
    padding: 0 0.5rem;
  }

  .chart-bar-container {
    min-width: 40px;
  }

  .bar-label {
    font-size: 0.65rem;
    padding-top: 0.25rem;
  }

  .bar-week {
    font-size: 0.6rem;
    margin-top: 0.25rem;
  }

  /* Loading/Error States */
  .loading-state,
  .error-state,
  .empty-state {
    padding: 2rem 1rem;
  }

  .loading-state p,
  .error-state p,
  .empty-state p {
    font-size: 0.875rem;
  }

  .error-icon,
  .empty-icon {
    font-size: 2rem;
  }
}

/* Extra small devices (very narrow screens) */
@media (max-width: 360px) {
  .progress-tracker {
    padding: 0.5rem;
  }

  .section-title {
    font-size: 1rem;
  }

  .timeframe-btn {
    padding: 0.45rem 0.6rem;
    font-size: 0.8rem;
  }

  .metric-card {
    padding: 0.75rem;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .metric-icon {
    width: 45px;
    height: 45px;
    font-size: 1.1rem;
  }

  .metric-value {
    font-size: 1.2rem;
  }

  .metric-label {
    font-size: 0.75rem;
  }

  .metric-change {
    font-size: 0.65rem;
    justify-content: center;
  }

  .breakdown-title,
  .chart-title {
    font-size: 0.95rem;
  }

  .chart-bars {
    gap: 0.25rem;
    padding: 0 0.5rem;
  }

  .chart-bar-container {
    min-width: 35px;
  }

  .bar-label {
    font-size: 0.6rem;
  }

  .bar-week {
    font-size: 0.55rem;
  }
}
</style>
